import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const MirrorContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  position: relative;
  padding: ${props => props.theme.spacing.xl} 0;
`;

const PrimaryText = styled(motion.h1)`
  font-size: clamp(5rem, 15vw, 12rem);
  font-weight: ${props => props.theme.typography.weights.extrabold};
  background: ${props => props.theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 0.9;
  letter-spacing: -0.04em;
  text-transform: uppercase;
  margin: 0;
  position: relative;
  z-index: 2;
  
  /* Subtle glow effect */
  filter: drop-shadow(0 0 30px ${props => props.theme.mode === 'dark' ? 'rgba(139, 92, 246, 0.3)' : 'rgba(139, 92, 246, 0.15)'});
`;

const Divider = styled(motion.div)`
  width: 100%;
  max-width: 400px;
  height: 2px;
  background: linear-gradient(90deg, transparent, ${props => props.theme.colors.accentPurple}, transparent);
  margin: ${props => props.theme.spacing.sm} 0;
  position: relative;
  z-index: 2;
`;

const ReflectedText = styled(motion.h2)`
  font-size: clamp(5rem, 15vw, 12rem);
  font-weight: ${props => props.theme.typography.weights.extrabold};
  background: linear-gradient(180deg, 
    ${props => props.theme.mode === 'dark' ? 'rgba(139, 92, 246, 0.75)' : 'rgba(139, 92, 246, 0.6)'} 0%, 
    ${props => props.theme.mode === 'dark' ? 'rgba(139, 92, 246, 0.35)' : 'rgba(139, 92, 246, 0.25)'} 60%,
    rgba(139, 92, 246, 0.05) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 0.9;
  letter-spacing: -0.04em;
  text-transform: uppercase;
  margin: 0;
  transform: scaleY(-1);
  position: relative;
  z-index: 1;
  
  /* Fade out effect */
  mask-image: linear-gradient(to bottom, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.1) 95%);
  -webkit-mask-image: linear-gradient(to bottom, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.1) 95%);
`;

const GlowOrb = styled(motion.div)`
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: ${props => props.theme.gradients.glow};
  filter: blur(60px);
  opacity: 0.5;
  pointer-events: none;
  z-index: 0;
`;

const MirrorText = ({ primaryWord = "NEAL", reflectedWord = "LEAN" }) => {
  return (
    <MirrorContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.3 }}
    >
      <GlowOrb
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <PrimaryText
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
        whileHover={{ scale: 1.02 }}
      >
        {primaryWord}
      </PrimaryText>

      <Divider
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.8, delay: 0.6 }}
      />

      <ReflectedText
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.8 }}
      >
        {reflectedWord}
      </ReflectedText>
    </MirrorContainer>
  );
};

export default MirrorText;
